import React, { useEffect, useState, useRef, useImperativeHandle, forwardRef } from 'react';
import { keycloak } from '../../../../lib/keycloak/index.ts';

// Update the WebSocketMessage interface to match the backend format
interface WebSocketMessage {
  type?: string;
  stage?: string;
  message?: string;
  progress?: number;
}

interface BrightyWebSocketProps {
  clientId: string;
  onMessage?: (message: WebSocketMessage) => void;
  onConnect?: () => void;
  onDisconnect?: () => void;
}

// Using forwardRef to expose methods to parent
const BrightyWebSocket = forwardRef<any, BrightyWebSocketProps>(({
  clientId,
  onMessage,
  onConnect,
  onDisconnect
}, ref) => {
  const [connected, setConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const socketRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<any>(null);

  // Expose methods to parent component via ref
  useImperativeHandle(ref, () => ({
    sendMessage: (question: string, precision: number, user: string, threadId: string) => {
      return sendMessage(question, precision, user, threadId);
    },
    connectWebSocket: () => {
      return connectWebSocket()
    }
  }));

  // Function to establish WebSocket connection
  const connectWebSocket = () => {
    // Clear any existing connection
    if (socketRef.current) {
      socketRef.current.close();
      socketRef.current = null;
    }

    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const host = window.location.hostname;
    const port = window.location.hostname === 'localhost' ? ':8094' : '';
    const socketUrl = `${protocol}//${host}${port}/api/agent/ws`;

    console.log('Connecting to WebSocket:', socketUrl);

    try {
      const socket = new WebSocket(socketUrl);
      socketRef.current = socket;

      socket.onopen = () => {
        console.log('WebSocket connected');
        setConnected(true);
        setError(null);
        if (onConnect) onConnect();

        socket.send(JSON.stringify({ type: 'auth', token: keycloak.token }));

        // Clear any reconnection timeout
        if (reconnectTimeoutRef.current) {
          clearTimeout(reconnectTimeoutRef.current);
          reconnectTimeoutRef.current = null;
        }
      };

      socket.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data) as WebSocketMessage;
          console.log('WebSocket message:', data);
          if (onMessage) onMessage(data);
        } catch (err) {
          console.error('Error parsing WebSocket message:', err);
        }
      };

      socket.onerror = (event) => {
        console.error('WebSocket error:', event);
        setError('WebSocket connection error');
      };

      socket.onclose = (ev) => {
        console.log('WebSocket closed', ev);
        setConnected(false);
        if (onDisconnect) onDisconnect();
      };
    } catch (err) {
      console.error('Error creating WebSocket:', err);
      setError('Failed to create WebSocket connection');
    }
  };

  // Function to send messages through WebSocket
  const sendMessage = (question: string, precision: number, user: string, threadId: string) => {
    if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
      const message = JSON.stringify({
        token: keycloak.token,
        question,
        precision,
        user,
        thread_id: threadId
      });

      console.log('Sending message:', message);
      socketRef.current.send(message);
      return true;
    } else {
      console.error('WebSocket not connected');
      setError('WebSocket not connected');
      return false;
    }
  };

  // Effect to connect WebSocket, with proper cleanup
  useEffect(() => {
    console.log('WebSocket component mounted');
    connectWebSocket();

    // Cleanup function
    return () => {
      console.log('WebSocket component unmounting, closing socket');
      if (socketRef.current) {
        socketRef.current.close();
        socketRef.current = null;
      }

      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
    };
  }, [clientId]); // Only reconnect when clientId changes

  // Not rendering anything visible
  return null;
});

export default BrightyWebSocket;